<template>
  <div
    :class="[
      'tir-pm-file-preview',
      {
        'is-small': viewVariant === FilePreviewVariantEnum.Small,
        'is-inline': viewVariant === FilePreviewVariantEnum.Inline,
        'is-loading': isLoading,
        'is-show-controls': isLoading || isOpenedConfirmDeletePopover,
        'is-proccessing': fileInfo.IsProccessing,
        'is-selected': isSelected,
      },
    ]"
    @click="onClick"
  >
    <template v-if="viewVariant !== FilePreviewVariantEnum.Inline">
      <div
        class="tir-pm-file-preview__wrapper-file"
        @click="openFilePreviewModal"
      >
        <img
          v-if="fileType === FileTypeEnum.Image"
          :src="fileInfo.Url"
          :draggable="false"
          class="tir-pm-file-preview__file tir-pm-file-preview__file--image"
        />
        <div
          v-else-if="fileType === FileTypeEnum.Other"
          class="tir-pm-file-preview__file tir-pm-file-preview__file--document"
        >
          <DocumentIcon></DocumentIcon>
          <div class="tir-pm-file-preview__preview-extension">
            .{{ fileExtension }}
          </div>
        </div>
        <div
          v-else
          class="tir-pm-file-preview__file tir-pm-file-preview__file--video"
        >
          <img
            v-if="fileInfo.VideoPreviewUrl"
            class="tir-pm-file-preview__video-preview-image"
            :src="fileInfo.VideoPreviewUrl"
          />
          <div
            class="tir-pm-file-preview__video-button"
            v-if="!fileInfo.IsProccessing && !isLoading"
          >
            <PlayCircleIcon class="tir-pm-file-preview__video-button-icon" />
          </div>
        </div>
      </div>
      <div
        class="tir-pm-file-preview__wrapper-info"
        v-if="variant !== FilePreviewVariantEnum.Small"
      >
        <div class="tir-pm-file-preview__wrapper-info-row">
          <div
            class="tir-pm-file-preview__user-info-round"
            v-if="
              fileInfo.User &&
              fileInfo.Progress === undefined &&
              !fileInfo.IsProccessing
            "
          >
            <TirPmIconView
              :size="24"
              :color-code="fileInfo.User.AvatarColor"
              :image-load-url="fileInfo.User.Image"
              is-rounded
            >
              <template #mainSlot>{{ computedFotoName }}</template>
            </TirPmIconView>
          </div>
          <div class="tir-pm-file-preview__inner-file-info">
            <div class="tir-pm-file-preview__wrapper-filename">
              <div
                :class="[
                  'tir-pm-file-preview__filename',
                  {
                    'full-width':
                      fileInfo.Progress !== undefined || fileInfo.IsProccessing,
                  },
                ]"
              >
                {{ fileName }}
              </div>
              <div class="tir-pm-file-preview__file-extension">
                .{{ fileExtension }}
              </div>
            </div>
            <div
              class="tir-pm-file-preview__wrapper-file-info"
              v-if="fileInfo.Progress === undefined && !fileInfo.IsProccessing"
            >
              <span class="tir-pm-file-preview__file-upload-time">
                {{ dateUploadString }}
              </span>
              <div class="tir-pm-file-preview__file-size" v-if="fileInfo.Size">
                {{ sizeString }}
              </div>
            </div>
          </div>
        </div>
        <FilePreviewProgress
          class="tir-pm-file-preview__progress"
          v-if="fileInfo.Progress !== undefined || fileInfo.IsProccessing"
          :variant="FilePreviewVariantEnum.Regular"
          :progress="fileInfo.Progress"
          :is-proccessing="fileInfo.IsProccessing"
        />
      </div>
      <div
        class="tir-pm-file-preview__small-progress"
        v-if="
          variant === FilePreviewVariantEnum.Small &&
          (fileInfo.Progress !== undefined || fileInfo.IsProccessing)
        "
      >
        <FilePreviewProgress
          :variant="FilePreviewVariantEnum.Small"
          :progress="fileInfo.Progress"
          :is-proccessing="fileInfo.IsProccessing"
        />
      </div>
      <div
        class="tir-pm-file-preview__list-controls"
        v-if="(isViewControls || isAccessDownload) && !hideControls"
      >
        <FilePreviewConfirmDeletePopover
          v-if="isAccessDelete"
          ref="$confirmDeletePopover"
          v-model:is-opened="isOpenedConfirmDeletePopover"
          :is-loading="isLoadingDelete"
          @on-click-delete="emitOnClickDelete"
        >
          <template #default>
            <TirPmButton
              :size="TirPmButtonSizeEnum.Small"
              :variant="TirPmButtonVariantEnum.LinkState"
              @click.stop="onClickDelete"
            >
              <template #leftSlot>
                <CrossCompactIcon />
              </template>
            </TirPmButton>
          </template>
        </FilePreviewConfirmDeletePopover>
        <FilePreviewEditNamePopover
          v-if="isAccessEdit"
          ref="$editNamePopover"
          v-model:is-opened="isOpenedEditNamePopover"
          :is-loading="filesLoadingEditName.includes(fileInfo.FileId)"
          :file-name="fileInfo.Name"
          @save-file-name="emit('saveName', fileInfo.FileId, $event)"
        >
          <template #default>
            <TirPmButton
              :size="TirPmButtonSizeEnum.Small"
              :variant="TirPmButtonVariantEnum.LinkState"
              @click.stop="onClickEdit"
            >
              <template #leftSlot>
                <PencilSquareIcon />
              </template>
            </TirPmButton>
          </template>
        </FilePreviewEditNamePopover>
        <TirPmButton
          v-if="isAccessDownload"
          :size="TirPmButtonSizeEnum.Small"
          :variant="TirPmButtonVariantEnum.LinkState"
          @click="download"
        >
          <template #leftSlot>
            <ArrowDownTrayIcon />
          </template>
        </TirPmButton>
      </div>
    </template>
    <template v-else>
      <FilePreviewProgress
        class="tir-pm-file-preview__inline-progress"
        v-if="fileInfo.Progress !== undefined || fileInfo.IsProccessing"
        :variant="FilePreviewVariantEnum.Inline"
        :progress="fileInfo.Progress"
        :is-proccessing="fileInfo.IsProccessing"
      />
      <DocumentIcon class="tir-pm-file-preview__inline-icon" />
      <span class="tir-pm-file-preview__inline-name">
        {{ fileName }}
      </span>
      <span class="tir-pm-file-preview__inline-file-ext">
        .{{ fileExtension }}
      </span>
      <div class="tir-pm-file-preview__inline-buttons" v-if="isViewControls">
        <FilePreviewConfirmDeletePopover
          v-if="isAccessDelete"
          ref="$confirmDeletePopover"
          v-model:is-opened="isOpenedConfirmDeletePopover"
          :is-loading="isLoadingDelete"
          @on-click-delete="emitOnClickDelete"
        >
          <template #default>
            <button
              class="tir-pm-file-preview__inline-button"
              @click.stop="onClickDelete"
            >
              <CrossCompactIcon />
            </button>
          </template>
        </FilePreviewConfirmDeletePopover>
        <FilePreviewEditNamePopover
          v-if="isAccessEdit"
          ref="$editNamePopover"
          v-model:is-opened="isOpenedEditNamePopover"
          :is-loading="filesLoadingEditName.includes(fileInfo.FileId)"
          :file-name="fileInfo.Name"
          @save-file-name="emit('saveName', fileInfo.FileId, $event)"
        >
          <template #default>
            <button
              class="tir-pm-file-preview__inline-button"
              @click.stop="onClickEdit"
            >
              <PencilSquareIcon />
            </button>
          </template>
        </FilePreviewEditNamePopover>
      </div>
    </template>

    <FilePreviewModal
      v-if="!isDisabledOpen && fileInfo.Progress === undefined"
      ref="$filePreviewModal"
      v-model:file="(previewFile as any)"
      v-model:full-screen="previewFullscreenModal"
      :files="filesForModal"
      :is-view-next="filesForModal.length > 1"
      :is-view-prev="filesForModal.length > 1"
      :files-loading-edit-name="filesLoadingEditName"
      @close="closeFilePreviewModal"
      @on-click-download="download"
      @on-click-next="previewNextFile"
      @on-click-prev="previewPrevFile"
      @on-click-edit="emit('onClickEdit', $event)"
      @save-name="emitSaveName"
    />
  </div>
</template>

<script lang="ts" setup generic="T extends IFilePreview">
import { computed, PropType, ref } from "vue";
import {
  TirPmIconView,
  TirPmButton,
  TirPmButtonSizeEnum,
  TirPmButtonVariantEnum,
} from "tir-components";
import {
  DocumentIcon,
  CrossCompactIcon,
  PencilSquareIcon,
  ArrowDownTrayIcon,
  PlayCircleIcon,
} from "tir-style-system/icons/outline";
import { IFilePreview } from "./interfaces";
import {
  formatFileSize,
  getDateUploadString,
  getFileExtension,
  downloadFile,
} from "./utils";
import { imageExtensions, videoExtensitons } from "./constants";
import {
  FilePreviewConfirmDeletePopover,
  FilePreviewEditNamePopover,
  FilePreviewModal,
  FilePreviewProgress,
} from "./components";
import {
  FileTypeEnum,
  FilePreviewAccessEnum,
  FilePreviewVariantEnum,
} from "./enums";

const emit = defineEmits<{
  /**Событие клика по кнопке удаления*/
  (e: "onClickDelete"): void;
  /**Событие клика по кнопке редактирования */
  (e: "onClickEdit", fileId: T["FileId"]): void;
  /**Событие сохранения названия файла */
  (e: "saveName", fileId: T["FileId"], filename: string): void;
}>();

const emitSaveName = (fileId: T["FileId"], filename: string) => {
  emit("saveName", fileId, filename);
};

const props = defineProps({
  /**Информация о файле */
  file: {
    type: Object as PropType<T>,
    required: true,
  },
  /**Весь список файлов */
  files: {
    type: Array as PropType<Array<T>>,
    default: [],
  },
  /**Вариант компонента */
  variant: {
    type: String as PropType<FilePreviewVariantEnum>,
    default: () => FilePreviewVariantEnum.Regular,
  },
  /**Вариант для файлов кроме медиа */
  noMediaVariant: {
    type: String as PropType<FilePreviewVariantEnum>,
    default: () => FilePreviewVariantEnum.Regular,
  },
  /**Загрузка при удалении */
  isLoadingDelete: {
    type: Boolean,
    default: false,
  },
  /**Загрузка при редактировании названия */
  filesLoadingEditName: {
    type: Array as PropType<Array<T["FileId"]>>,
    default: [],
  },
  /**Отключить открытие модального окна */
  isDisabledOpen: {
    type: Boolean,
    default: false,
  },
  /**Скрыть контролы */
  hideControls: {
    type: Boolean,
    default: false,
  },
  /**Состояние выбранного файла */
  isSelected: {
    type: Boolean,
    default: false,
  },
});
/**Режим полного экрана для модалки */
const previewFullscreenModal = ref(false);
/**Информация о файле */
const fileInfo = computed(() => props.file as IFilePreview);
/**Состояние загрузки файла */
const isLoading = computed(
  () => fileInfo.value.Progress !== undefined && !fileInfo.value.IsProccessing
);
/**Имя пользователя который загрузил фото для аватарки */
const computedFotoName = computed(() => {
  if (!fileInfo.value.User) return;
  const { FirstName, LastName } = fileInfo.value.User;

  return `${FirstName?.[0]?.toLocaleUpperCase()}${LastName?.[0]?.toLocaleUpperCase()}`;
});
/**Расщирение файла */
const fileExtension = computed(() => {
  return getFileExtension(fileInfo.value.Name);
});
/**Название файла (без расширения) */
const fileName = computed(() => {
  const lastDotIndex = fileInfo.value.Name.lastIndexOf(".");
  if (lastDotIndex === -1) return fileInfo.value.Name; // Если точки нет, возвращаем исходное имя
  return fileInfo.value.Name.substring(0, lastDotIndex);
});
/**Тип файла */
const fileType = computed(() => {
  switch (true) {
    case imageExtensions.includes(fileExtension.value):
      return FileTypeEnum.Image;
    case videoExtensitons.includes(fileExtension.value):
      return FileTypeEnum.Video;
    default:
      return FileTypeEnum.Other;
  }
});
/**Разрешено удаление/отмена */
const isAccessDelete = computed(() =>
  fileInfo.value.Accesses?.includes(FilePreviewAccessEnum.Remove)
);
/**Ссылка на поповер подтверждения удаления */
const $confirmDeletePopover =
  ref<InstanceType<typeof FilePreviewConfirmDeletePopover>>();
/**Открыт ли поповер с подтверждением удаления */
const isOpenedConfirmDeletePopover = ref(false);
/**Отправить событие клика на кнопку удаления */
const emitOnClickDelete = () => {
  if (props.isLoadingDelete) return;
  emit("onClickDelete");
};
/**При клике на кнопку удаления */
const onClickDelete = () => {
  if (fileInfo.value.IsProccessing || !isAccessDelete.value) return;
  if (fileInfo.value.Progress !== undefined) {
    emitOnClickDelete();
  } else {
    $editNamePopover.value?.close();
    $confirmDeletePopover.value?.open();
  }
};
/**Разрешено редактирование */
const isAccessEdit = computed(
  () =>
    fileInfo.value.Accesses?.includes(FilePreviewAccessEnum.Edit) &&
    !isLoading.value
);
/**Ссылка на поповер редактирования названия файла */
const $editNamePopover = ref<InstanceType<typeof FilePreviewEditNamePopover>>();
/**Открыт ли поповер редактирования названия */
const isOpenedEditNamePopover = ref(false);
/**При клике на кнопку редактирования */
const onClickEdit = () => {
  // Если изображение
  if (
    imageExtensions.includes(fileExtension.value) &&
    !["gif", "svg"].includes(fileExtension.value)
  ) {
    emit("onClickEdit", fileInfo.value.FileId);
  } else {
    $confirmDeletePopover.value?.close();
    $editNamePopover.value?.open();
  }
};
/**Разрешено ли скачивать */
const isAccessDownload = computed(
  () =>
    !fileInfo.value.IsProccessing &&
    !isLoading.value &&
    fileType.value !== FileTypeEnum.Video
);
/**Видимость контролов */
const isViewControls = computed(
  () =>
    !props.hideControls &&
    !fileInfo.value.IsProccessing &&
    (isAccessDelete.value || isAccessEdit.value)
);
/**Отображаемый вариант файла */
const viewVariant = computed(() => {
  if (fileType.value === FileTypeEnum.Other) return props.noMediaVariant;
  return props.variant;
});
/**Дата загрузки в виде строки */
const dateUploadString = computed(
  () =>
    fileInfo.value.DateUpload && getDateUploadString(fileInfo.value.DateUpload)
);
/**Размер файла в виде строки */
const sizeString = computed(
  () => fileInfo.value.Size && formatFileSize(fileInfo.value.Size)
);

/**Загрузка файла */
const download = () => {
  downloadFile(fileInfo.value.Url, `${fileInfo.value.Name}`);
};

/**Превью файла */
const previewFile = ref<IFilePreview>();
/**Открыть модальное окно с превью файла */
const openFilePreviewModal = () => {
  if (
    props.isDisabledOpen ||
    fileInfo.value.Progress !== undefined ||
    fileInfo.value.IsProccessing
  )
    return;
  if (fileType.value === FileTypeEnum.Other) {
    download();
    return;
  }

  previewFile.value = fileInfo.value;
};
/**Закрытие модального окна предпросмотра */
const closeFilePreviewModal = () => {
  previewFile.value = undefined;
};
/**Список медиафайлов */
const filesForModal = computed(() => {
  const mediaFiles = props.files.filter((file) => {
    const ext = getFileExtension(file.Name);
    return imageExtensions.includes(ext) || videoExtensitons.includes(ext);
  });

  return mediaFiles.filter(
    (file) => file.Progress === undefined && !file.IsProccessing
  );
});
/**Индекс превью файла в списке медиафайлов */
const currentPreviewFileIndex = computed(() => {
  if (!filesForModal.value.length || !previewFile.value) return -1;
  return filesForModal.value.findIndex(
    (file) => file.FileId === previewFile.value.FileId
  );
});

const previewNextFile = () => {
  if (currentPreviewFileIndex.value === filesForModal.value.length - 1) {
    previewFile.value = filesForModal.value[0];
    return;
  }
  previewFile.value = filesForModal.value[currentPreviewFileIndex.value + 1];
};

const previewPrevFile = () => {
  if (currentPreviewFileIndex.value === 0) {
    previewFile.value = filesForModal.value[filesForModal.value.length - 1];
    return;
  }
  previewFile.value = filesForModal.value[currentPreviewFileIndex.value - 1];
};

/**Клик по компонену */
const onClick = () => {
  if (viewVariant.value !== FilePreviewVariantEnum.Inline) return;

  openFilePreviewModal();
};
</script>

<style lang="scss" scoped>
@import "./tir-pm-file-preview.scss";
</style>
